import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  FlatList,
  Modal,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  Alert,
  Dimensions,
  Image,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../../../utils/apiClient';

const { width, height } = Dimensions.get('window');

// ────────────────────────────────────────────────────────────────────────────
// Map a DB job_offers row (with candidate/job JOIN fields) into the rich
// shape this screen's UI expects. Real columns come from the new
// /api/recruitment/offers endpoint; UI-only computed fields (acceptance
// probability, complexity, negotiation rounds, smart-contract status) are
// left as sensible defaults — the screen renders them but they aren't load-
// bearing for the offer record itself.
// ────────────────────────────────────────────────────────────────────────────
const normaliseOffer = (row) => {
  if (!row) return null;
  const salary = Number(row.salary || 0);
  const bonus  = Number(row.bonus  || 0);
  // Stored offers don't track equity as a number, so totalPackage is
  // base + bonus only. The UI's "stock options" column just renders 0
  // when equity is absent, which is the honest answer.
  const totalPackage = salary + bonus;
  return {
    id: String(row.id),
    applicationId: row.application_id,
    name:           row.candidate_name || 'Applicant',
    email:          row.candidate_email || '',
    phone:          row.candidate_phone || '',
    avatar:         row.candidate_avatar || null,
    position:       row.position || row.job_title || '',
    currentCompany: '',                                  // not stored on the offer
    location:       row.candidate_location || row.job_location || '',
    department:     row.department || '',
    experience:     0,
    education:      '',
    skills:         [],
    // Offer details — the real fields
    offerStatus:     row.status || 'draft',
    contractType:    row.contract_type || 'permanent',
    baseSalary:      salary,
    bonus,
    stockOptions:    0,
    totalPackage,
    salaryCurrency:  row.salary_currency || 'GBP',
    offerScore:      row.offer_score || null,
    acceptanceProbability: row.acceptance_probability || null,
    offerDate:       row.created_at || row.sent_at || '',
    responseDeadline: row.expiry_date || null,
    startDate:       row.start_date || null,
    benefits:        typeof row.benefits === 'string' ? row.benefits.split(/[,;\n]/).map(s=>s.trim()).filter(Boolean) : (row.benefits || []),
    terms:           row.terms || '',
    offerLetter:     row.offer_letter || '',
    offerLetterUrl:  row.offer_letter_url || '',
    employerName:    row.employer_name || '',
    employerLogo:    row.employer_logo || null,
    jobId:           row.job_id,
    jobTitle:        row.job_title || '',
    // Response tracking
    sentAt:      row.sent_at || null,
    viewedAt:    row.viewed_at || null,
    acceptedAt:  row.accepted_at || null,
    declinedAt:  row.declined_at || null,
    responseMessage: row.response_message || '',
    // History — UI uses these but real one isn't tracked separately yet
    negotiationHistory: [],
    negotiationComplexity: 'Low',
    competitiveRisk: 'Low',
    raw: row,
  };
};

export default function JobOffersPipelineScreen({ navigation, route }) {
  // Inbound route params — passed in when the recruiter comes from a
  // specific job's pipeline. Default to empty so downstream navigation
  // calls never reference undeclared variables (ReferenceError).
  const jobId        = route?.params?.jobId        || null;
  const jobTitle     = route?.params?.jobTitle     || '';
  const companyLogo  = route?.params?.companyLogo  || null;
  const companyName  = route?.params?.companyName  || '';

  const [candidates, setCandidates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTab, setSelectedTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('offer_value');
  const [showSmartContractModal, setShowSmartContractModal] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // Smart Contract templates
  const smartContractTemplates = [
    {
      id: 'senior_engineer',
      name: 'Senior Engineer Contract',
      description: 'Comprehensive contract for senior engineering positions',
      clauses: ['Salary & Benefits', 'Stock Options', 'Remote Work', 'Performance Bonuses', 'IP Rights'],
      duration: 'Permanent',
      probationPeriod: '6 months',
      noticePeriod: '3 months',
    },
    {
      id: 'executive_contract',
      name: 'Executive Contract',
      description: 'Executive-level contract with comprehensive benefits',
      clauses: ['Executive Compensation', 'Equity Package', 'Severance Terms', 'Non-Compete', 'Board Participation'],
      duration: 'Permanent',
      probationPeriod: '12 months',
      noticePeriod: '6 months',
    },
    {
      id: 'consultant_contract',
      name: 'Consultant Contract',
      description: 'Flexible contract for consultant positions',
      clauses: ['Hourly Rate', 'Project Scope', 'Deliverables', 'Payment Terms', 'Confidentiality'],
      duration: '12 months',
      probationPeriod: 'N/A',
      noticePeriod: '1 month',
    },
    {
      id: 'graduate_contract',
      name: 'Graduate Contract',
      description: 'Entry-level contract for graduate positions',
      clauses: ['Training Program', 'Mentorship', 'Career Development', 'Performance Reviews', 'Benefits'],
      duration: 'Permanent',
      probationPeriod: '6 months',
      noticePeriod: '1 month',
    },
  ];


  // Derive tab counts from real loaded offers so the UI reflects DB state,
  // not the original mock counts. status comes from job_offers.status,
  // which we normalised to lowercase across the codebase.
  const tabs = (() => {
    const count = (predicate) => candidates.filter(predicate).length;
    return [
      { id: 'all',         name: 'All',         count: candidates.length },
      { id: 'draft',       name: 'Draft',       count: count(c => c.offerStatus === 'draft' || c.offerStatus === 'pending') },
      { id: 'sent',        name: 'Sent',        count: count(c => c.offerStatus === 'sent') },
      { id: 'negotiating', name: 'Negotiating', count: count(c => c.offerStatus === 'negotiating') },
      { id: 'accepted',    name: 'Accepted',    count: count(c => c.offerStatus === 'accepted') },
      { id: 'declined',    name: 'Declined',    count: count(c => c.offerStatus === 'declined' || c.offerStatus === 'rejected' || c.offerStatus === 'withdrawn') },
    ];
  })();

  const sortOptions = [
    { id: 'offer_value', name: 'Offer Value', icon: 'currency-gbp' },
    { id: 'offer_date', name: 'Offer Date', icon: 'calendar' },
    { id: 'deadline', name: 'Deadline', icon: 'clock' },
    { id: 'name', name: 'Name', icon: 'account' },
    { id: 'acceptance_probability', name: 'Acceptance Probability', icon: 'percent' },
  ];

  useEffect(() => {
    loadCandidates();
  }, []);

  const loadCandidates = async () => {
    setIsLoading(true);
    try {
      const cid = parseInt(await AsyncStorage.getItem('company_id') || '0', 10);
      if (!cid) {
        setCandidates([]);
        Alert.alert('No company', 'Could not determine your company. Please sign in again.');
        return;
      }
      // Try the rich /api/recruitment/offers endpoint first — it returns
      // job_offers JOINed with applications/users/jobs. When it 5xx's
      // (currently broken server-side), fall back to the raw job_offers
      // table so the screen still shows real data instead of going blank.
      let rows = [];
      try {
        const res = await apiFetch(`/api/recruitment/offers?company_id=${cid}&limit=200`);
        rows = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      } catch (e) {
        const status = e?.status || e?.response?.status;
        if (status >= 500 || !status) {
          console.warn('[MakeOffer] /api/recruitment/offers failed, falling back to /api/data/job_offers');
          const fb = await apiFetch(`/api/data/job_offers?company_id=${cid}&limit=200`);
          rows = Array.isArray(fb) ? fb : Array.isArray(fb?.data) ? fb.data : [];
        } else {
          throw e;
        }
      }
      console.log('[MakeOffer] loaded', rows.length, 'offers');
      const normalized = rows.map(normaliseOffer).filter(Boolean);
      setCandidates(normalized);
      await AsyncStorage.setItem(`offers_cache_${cid}`, JSON.stringify(normalized)).catch(() => {});
    } catch (error) {
      console.warn('loadCandidates:', error?.message);
      // Offline cache only — never mock
      const cid = parseInt(await AsyncStorage.getItem('company_id') || '0', 10);
      const cached = await AsyncStorage.getItem(`offers_cache_${cid}`);
      if (cached) { try { setCandidates(JSON.parse(cached)); } catch { setCandidates([]); } }
      else setCandidates([]);
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadCandidates();
    setRefreshing(false);
  };

  const filteredCandidates = candidates.filter(candidate => {
    const matchesTab = selectedTab === 'all' || candidate.offerStatus === selectedTab;
    
    const matchesSearch = String(candidate.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         String(candidate.position ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         String(candidate.currentCompany ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         String(candidate.department ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         String(candidate.contractType ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         candidate.skills.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesTab && matchesSearch;
  });

  const sortedCandidates = [...filteredCandidates].sort((a, b) => {
    switch (sortBy) {
      case 'offer_value':
        return b.totalPackage - a.totalPackage;
      case 'offer_date':
        return new Date(b.offerDate) - new Date(a.offerDate);
      case 'deadline':
        return new Date(a.responseDeadline) - new Date(b.responseDeadline);
      case 'name':
        return a.name.localeCompare(b.name);
      case 'acceptance_probability':
        return b.acceptanceProbability - a.acceptanceProbability;
      default:
        return 0;
    }
  });

  const getOfferStatusColor = (status) => {
    switch (status) {
      case 'draft': return '#6B7280';
      case 'pending_approval': return '#F59E0B';
      case 'sent': return '#3B82F6';
      case 'negotiating': return '#8B5CF6';
      case 'accepted': return '#10B981';
      case 'declined': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getOfferStatusText = (status) => {
    switch (status) {
      case 'draft': return 'DRAFT';
      case 'pending_approval': return 'PENDING APPROVAL';
      case 'sent': return 'SENT';
      case 'negotiating': return 'NEGOTIATING';
      case 'accepted': return 'ACCEPTED';
      case 'declined': return 'DECLINED';
      default: return status.toUpperCase();
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'Low': return '#10B981';
      case 'Medium': return '#F59E0B';
      case 'High': return '#EF4444';
      case 'Critical': return '#DC2626';
      default: return '#6B7280';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getDaysUntilDeadline = (deadlineString) => {
    const deadline = new Date(deadlineString);
    const today = new Date();
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <MaterialCommunityIcons
          key={i}
          name={i <= rating ? 'star' : 'star-outline'}
          size={14}
          color={i <= rating ? '#F59E0B' : '#D1D5DB'}
        />
      );
    }
    return stars;
  };

  const handleCandidatePress = (candidate) => {
    navigation.navigate('JobOfferDetailsScreen', { candidate });
  };

  const handleSelectCandidate = (candidateId) => {
    setSelectedCandidates(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      } else {
        return [...prev, candidateId];
      }
    });
  };

  const handleGenerateSmartContract = (candidate) => {
    Alert.alert(
      'Generate Smart Contract',
      `Generate smart contract for ${candidate.name}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Generate',
          onPress: async () => {
            try {
              // Only persist if this candidate row backs a real DB offer.
              // Otherwise the address would be orphaned (no offer to attach to).
              if (!candidate.raw || !candidate.id || !/^\d+$/.test(String(candidate.id))) {
                Alert.alert('Send the offer first', 'Create the offer before generating a smart contract.');
                return;
              }
              // Pseudo-deterministic address: ts + offer id. The real chain
              // integration lands later; this records intent + lets the UI
              // show "Generated" without lying about being on-chain.
              const address = `0x${Date.now().toString(16)}${String(candidate.id).padStart(6, '0')}`;
              const templateName = candidate.contractTemplate?.name || '';
              const res = await apiFetch(`/api/data/job_offers/${candidate.id}`, {
                method: 'PUT',
                body: JSON.stringify({
                  smart_contract_address: address,
                  notes: templateName ? `Smart contract template: ${templateName}` : undefined,
                }),
              });
              if (res?.error) throw new Error(res.message || 'DB update failed');
              setCandidates(prev => prev.map(c =>
                c.id === candidate.id ? { ...c, smartContractAddress: address, smartContractStatus: 'Generated' } : c
              ));
              Alert.alert('Generated', `Smart contract assigned: ${address.slice(0, 14)}…`);
            } catch (e) {
              console.warn('handleGenerateSmartContract:', e?.message);
              Alert.alert('Could not generate', e?.message || 'Please try again.');
            }
          }
        },
      ]
    );
  };

  const handleSendOffer = (candidate) => {
    Alert.alert(
      'Send Job Offer',
      `Send job offer to ${candidate.name}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Send Offer',
          onPress: async () => {
            try {
              // Two paths: the candidate row represents (a) an existing offer
              // in draft/pending state → flip status to 'sent'; (b) a
              // prospective applicant without an offer yet → create the offer.
              if (candidate.raw && candidate.id && /^\d+$/.test(String(candidate.id))) {
                // Existing offer row — update status
                const res = await apiFetch(`/api/data/job_offers/${candidate.id}`, {
                  method: 'PUT',
                  body: JSON.stringify({
                    status: 'sent',
                    sent_at: new Date().toISOString(),
                  }),
                });
                if (res?.error) throw new Error(res.message || 'Update failed');
                setCandidates(prev => prev.map(c =>
                  c.id === candidate.id ? { ...c, offerStatus: 'sent', sentAt: new Date().toISOString() } : c
                ));
                Alert.alert('Sent', `Offer sent to ${candidate.name}.`);
              } else {
                // No offer record yet — create one
                const cid = parseInt(await AsyncStorage.getItem('company_id') || '0', 10);
                const payload = {
                  application_id: candidate.applicationId || null,
                  job_id:         candidate.jobId || null,
                  company_id:     cid,
                  candidate_name: candidate.name,
                  candidate_email: candidate.email,
                  position:        candidate.position || candidate.jobTitle || '',
                  salary:          candidate.baseSalary || 0,
                  bonus:           candidate.bonus || 0,
                  salary_currency: candidate.salaryCurrency || 'GBP',
                  benefits:        Array.isArray(candidate.benefits) ? candidate.benefits.join(', ') : (candidate.benefits || ''),
                  start_date:      candidate.startDate || null,
                  expiry_date:     candidate.responseDeadline || null,
                  status:          'sent',
                  sent_at:         new Date().toISOString(),
                };
                const res = await apiFetch('/api/data/job_offers', {
                  method: 'POST',
                  body: JSON.stringify(payload),
                });
                if (res?.error) throw new Error(res.message || 'Create failed');
                await loadCandidates();
                // Hand off into the manage-offer workflow so the recruiter
                // tracks status, candidate response, negotiations from the
                // canonical screen.
                Alert.alert(
                  'Sent',
                  `Offer created and sent to ${candidate.name}.`,
                  [
                    { text: 'Stay here', style: 'cancel' },
                    { text: 'Manage offers', onPress: () => {
                      navigation.navigate('JobOfferManagementScreen', {
                        applicationId: candidate.id,
                        applicantId:   candidate.id,
                        applicant:     candidate,
                        applicantName: candidate.name,
                        applicantEmail:candidate.email,
                        position:      candidate.position || candidate.jobTitle,
                        offerId:       res?.id || null,
                        jobId, jobTitle, companyLogo, companyName,
                      });
                    } },
                  ]
                );
              }
            } catch (e) {
              console.warn('handleSendOffer:', e?.message);
              Alert.alert('Could not send offer', e?.message || 'Please try again.');
            }
          }
        },
      ]
    );
  };

  const handleBulkAction = (action) => {
    if (selectedCandidates.length === 0) {
      Alert.alert('No Selection', 'Please select candidates first');
      return;
    }

    Alert.alert(
      'Bulk Action',
      `${action} ${selectedCandidates.length} selected candidates?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Confirm',
          onPress: async () => {
            // Pull the actual offer rows from state so we can hit /api/data
            // with their numeric ids (string-id mock rows are skipped).
            const rows = candidates.filter(c =>
              selectedCandidates.includes(c.id) && c.raw && /^\d+$/.test(String(c.id))
            );
            const skipped = selectedCandidates.length - rows.length;

            // Map bulk-action labels to the column writes they imply.
            const writeFor = (a) => {
              if (a === 'Move to Accepted Offers') {
                return { status: 'accepted', accepted_date: new Date().toISOString().slice(0, 10) };
              }
              if (a === 'Send Offers' || a === 'Send') {
                return { status: 'sent', email_sent: true, email_sent_date: new Date().toISOString() };
              }
              if (a === 'Withdraw Offers' || a === 'Withdraw') {
                return { status: 'withdrawn' };
              }
              return null;
            };
            const fields = writeFor(action);

            let okCount = 0;
            let failCount = 0;
            if (fields) {
              await Promise.all(rows.map(async (c) => {
                try {
                  const res = await apiFetch(`/api/data/job_offers/${c.id}`, {
                    method: 'PUT',
                    body: JSON.stringify(fields),
                  });
                  if (res?.error) throw new Error(res.message || 'PUT failed');
                  okCount++;
                } catch (e) {
                  console.warn(`bulk ${action} offer ${c.id}:`, e?.message);
                  failCount++;
                }
              }));
            }

            console.log(`[MakeOffer] bulk ${action}: ok=${okCount} fail=${failCount} skipped=${skipped}`);
            setSelectedCandidates([]);

            // Refresh from server so the UI reflects the new statuses.
            if (okCount > 0) await loadCandidates();

            // Notify + optionally hand off to the next stage.
            const msg = `${okCount} updated${failCount ? `, ${failCount} failed` : ''}${skipped ? `, ${skipped} skipped (not yet saved)` : ''}.`;
            if (action === 'Move to Accepted Offers') {
              Alert.alert('Done', msg, [
                { text: 'Stay', style: 'cancel' },
                { text: 'View Accepted', onPress: () => navigation.navigate('AcceptedOffersScreen', {
                  jobId, jobTitle, companyLogo, companyName,
                }) },
              ]);
            } else if (action === 'Manage Offers') {
              navigation.navigate('JobOfferManagementScreen', {
                jobId, jobTitle, companyLogo, companyName,
              });
            } else {
              Alert.alert('Done', msg);
            }
          }
        },
      ]
    );
  };

  const renderCandidateCard = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.candidateCard,
        selectedCandidates.includes(item.id) && styles.selectedCard,
      ]}
      onPress={() => handleCandidatePress(item)}
      activeOpacity={0.7}
    >
      {/* Selection Checkbox */}
      <TouchableOpacity
        style={styles.checkbox}
        onPress={() => handleSelectCandidate(item.id)}
      >
        <MaterialCommunityIcons
          name={selectedCandidates.includes(item.id) ? 'checkbox-marked' : 'checkbox-blank-outline'}
          size={24}
          color={selectedCandidates.includes(item.id) ? '#8B5CF6' : '#D1D5DB'}
        />
      </TouchableOpacity>

      {/* Candidate Header */}
      <View style={styles.candidateHeader}>
        <Image source={{ uri: (item.avatar && item.avatar.startsWith('http')) ? item.avatar : undefined }} style={styles.avatar} />
        <View style={styles.candidateInfo}>
          <View style={styles.candidateNameContainer}>
            <Text style={styles.candidateName}>{item.name}</Text>
            <View style={styles.candidateBadges}>
              {item.tags.map((tag, index) => (
                <View key={index} style={styles.tagBadge}>
                  <MaterialCommunityIcons name="file-document-outline" size={10} color="#8B5CF6" />
                  <Text style={styles.tagText}>{tag}</Text>
                </View>
              ))}
            </View>
          </View>
          <Text style={styles.candidatePosition}>{item.position}</Text>
          <Text style={styles.candidateCompany}>{item.currentCompany} • {item.department}</Text>
        </View>
        <View style={styles.candidateStatus}>
          <View style={[styles.statusBadge, { backgroundColor: getOfferStatusColor(item.offerStatus) }]}>
            <Text style={styles.statusText}>{getOfferStatusText(item.offerStatus)}</Text>
          </View>
        </View>
      </View>

      {/* Offer Package Summary */}
      <View style={styles.offerPackageContainer}>
        <View style={styles.offerPackageHeader}>
          <MaterialCommunityIcons name="currency-gbp" size={20} color="#8B5CF6" />
          <Text style={styles.offerPackageTitle}>Total Compensation Package</Text>
          <View style={styles.packageValueBadge}>
            <Text style={styles.packageValueText}>{formatCurrency(item.totalPackage)}</Text>
          </View>
        </View>
        
        {/* Package Breakdown */}
        <View style={styles.packageBreakdown}>
          <View style={styles.packageItem}>
            <MaterialCommunityIcons name="cash" size={16} color="#10B981" />
            <Text style={styles.packageLabel}>Base Salary</Text>
            <Text style={styles.packageValue}>{formatCurrency(item.baseSalary)}</Text>
          </View>
          <View style={styles.packageItem}>
            <MaterialCommunityIcons name="gift" size={16} color="#F59E0B" />
            <Text style={styles.packageLabel}>Bonus</Text>
            <Text style={styles.packageValue}>{formatCurrency(item.bonus)}</Text>
          </View>
          <View style={styles.packageItem}>
            <MaterialCommunityIcons name="chart-line" size={16} color="#3B82F6" />
            <Text style={styles.packageLabel}>Stock Options</Text>
            <Text style={styles.packageValue}>{formatCurrency(item.stockOptions)}</Text>
          </View>
        </View>
      </View>

      {/* Smart Contract Section */}
      <View style={styles.smartContractContainer}>
        <View style={styles.smartContractHeader}>
          <MaterialCommunityIcons name="file-document-outline" size={20} color="#8B5CF6" />
          <Text style={styles.smartContractTitle}>Smart Contract</Text>
          <View style={[
            styles.contractStatusBadge,
            { backgroundColor: item.digitalSignature ? '#10B981' : item.legalReview ? '#F59E0B' : '#6B7280' }
          ]}>
            <Text style={styles.contractStatusText}>
              <Text>{item.digitalSignature ? 'SIGNED' : item.legalReview ? 'REVIEW' : 'DRAFT'}</Text>
            </Text>
          </View>
        </View>
        
        {/* Contract Details */}
        <View style={styles.contractDetails}>
          <Text style={styles.contractTemplateName}>{item.contractTemplate.name}</Text>
          <Text style={styles.contractDescription}>{item.contractTemplate.description}</Text>
          
          {/* Contract Features */}
          <View style={styles.contractFeatures}>
            <View style={styles.contractFeature}>
              <MaterialCommunityIcons 
                name={item.contractGenerated ? 'check-circle' : 'clock-outline'} 
                size={14} 
                color={item.contractGenerated ? '#10B981' : '#F59E0B'} 
              />
              <Text style={styles.contractFeatureText}>Contract Generated</Text>
            </View>
            <View style={styles.contractFeature}>
              <MaterialCommunityIcons 
                name={item.legalReview ? 'check-circle' : 'clock-outline'} 
                size={14} 
                color={item.legalReview ? '#10B981' : '#F59E0B'} 
              />
              <Text style={styles.contractFeatureText}>Legal Review</Text>
            </View>
            <View style={styles.contractFeature}>
              <MaterialCommunityIcons 
                name={item.digitalSignature ? 'check-circle' : 'clock-outline'} 
                size={14} 
                color={item.digitalSignature ? '#10B981' : '#F59E0B'} 
              />
              <Text style={styles.contractFeatureText}>Digital Signature</Text>
            </View>
            <View style={styles.contractFeature}>
              <MaterialCommunityIcons 
                name={item.blockchainVerified ? 'check-circle' : 'clock-outline'} 
                size={14} 
                color={item.blockchainVerified ? '#8B5CF6' : '#9CA3AF'} 
              />
              <Text style={styles.contractFeatureText}>Blockchain Verified</Text>
            </View>
          </View>
          
          {/* Smart Contract Address */}
          {item.smartContractAddress && (
            <View style={styles.contractAddressContainer}>
              <MaterialCommunityIcons name="link" size={14} color="#8B5CF6" />
              <Text style={styles.contractAddressLabel}>Contract Address:</Text>
              <Text style={styles.contractAddress}>{item.smartContractAddress.substring(0, 10)}...</Text>
            </View>
          )}
        </View>
      </View>

      {/* Offer Timeline */}
      <View style={styles.offerTimelineContainer}>
        <View style={styles.timelineHeader}>
          <MaterialCommunityIcons name="timeline" size={18} color="#6B7280" />
          <Text style={styles.timelineTitle}>Offer Timeline</Text>
        </View>
        
        <View style={styles.timelineItems}>
          <View style={styles.timelineItem}>
            <MaterialCommunityIcons name="calendar-plus" size={16} color="#3B82F6" />
            <Text style={styles.timelineLabel}>Offer Date:</Text>
            <Text style={styles.timelineValue}>{formatDate(item.offerDate)}</Text>
          </View>
          <View style={styles.timelineItem}>
            <MaterialCommunityIcons name="calendar-clock" size={16} color="#EF4444" />
            <Text style={styles.timelineLabel}>Response Deadline:</Text>
            <Text style={[
              styles.timelineValue,
              { color: getDaysUntilDeadline(item.responseDeadline) <= 3 ? '#EF4444' : '#374151' }
            ]}>
              {formatDate(item.responseDeadline)} ({getDaysUntilDeadline(item.responseDeadline)} days)
            </Text>
          </View>
          <View style={styles.timelineItem}>
            <MaterialCommunityIcons name="calendar-start" size={16} color="#10B981" />
            <Text style={styles.timelineLabel}>Start Date:</Text>
            <Text style={styles.timelineValue}>{formatDate(item.startDate)}</Text>
          </View>
        </View>
      </View>

      {/* Negotiation Status */}
      {item.negotiationRounds > 0 && (
        <View style={styles.negotiationContainer}>
          <View style={styles.negotiationHeader}>
            <MaterialCommunityIcons name="handshake" size={18} color="#8B5CF6" />
            <Text style={styles.negotiationTitle}>Negotiation Status</Text>
            <View style={styles.negotiationRoundsBadge}>
              <Text style={styles.negotiationRoundsText}>{item.negotiationRounds} rounds</Text>
            </View>
          </View>
          
          <View style={styles.negotiationDetails}>
            <Text style={styles.negotiationPoint}>Current Point: {item.currentNegotiationPoint}</Text>
            <Text style={styles.negotiationComplexity}>
              Complexity: <Text style={{ color: getUrgencyColor(item.negotiationComplexity) }}>
                {item.negotiationComplexity}
              </Text>
            </Text>
          </View>
        </View>
      )}

      {/* Offer Metrics */}
      <View style={styles.offerMetricsContainer}>
        <View style={styles.metricsHeader}>
          <Text style={styles.metricsTitle}>Offer Analytics</Text>
        </View>
        
        <View style={styles.metricsGrid}>
          <View style={styles.metricItem}>
            <MaterialCommunityIcons name="percent" size={16} color="#10B981" />
            <Text style={styles.metricLabel}>Acceptance Probability</Text>
            <Text style={[
              styles.metricValue,
              { color: item.acceptanceProbability >= 80 ? '#10B981' : '#F59E0B' }
            ]}>
              {item.acceptanceProbability}%
            </Text>
          </View>
          <View style={styles.metricItem}>
            <MaterialCommunityIcons name="alert" size={16} color={getUrgencyColor(item.urgencyLevel)} />
            <Text style={styles.metricLabel}>Urgency</Text>
            <Text style={[styles.metricValue, { color: getUrgencyColor(item.urgencyLevel) }]}>
              {item.urgencyLevel}
            </Text>
          </View>
          <View style={styles.metricItem}>
            <MaterialCommunityIcons name="account-multiple" size={16} color="#EF4444" />
            <Text style={styles.metricLabel}>Competitor Offers</Text>
            <Text style={styles.metricValue}>{item.competitorOffers}</Text>
          </View>
          <View style={styles.metricItem}>
            <MaterialCommunityIcons name="chart-line" size={16} color="#3B82F6" />
            <Text style={styles.metricLabel}>Market Rate</Text>
            <Text style={styles.metricValue}>{formatCurrency(item.marketRate)}</Text>
          </View>
        </View>
      </View>

      {/* Approval Status */}
      <View style={styles.approvalContainer}>
        <Text style={styles.approvalTitle}>Approval Status</Text>
        <View style={styles.approvalItems}>
          <View style={styles.approvalItem}>
            <MaterialCommunityIcons 
              name={item.hrApproval ? 'check-circle' : 'clock-outline'} 
              size={14} 
              color={item.hrApproval ? '#10B981' : '#F59E0B'} 
            />
            <Text style={styles.approvalText}>HR</Text>
          </View>
          <View style={styles.approvalItem}>
            <MaterialCommunityIcons 
              name={item.managerApproval ? 'check-circle' : 'clock-outline'} 
              size={14} 
              color={item.managerApproval ? '#10B981' : '#F59E0B'} 
            />
            <Text style={styles.approvalText}>Manager</Text>
          </View>
          <View style={styles.approvalItem}>
            <MaterialCommunityIcons 
              name={item.financeApproval ? 'check-circle' : 'clock-outline'} 
              size={14} 
              color={item.financeApproval ? '#10B981' : '#F59E0B'} 
            />
            <Text style={styles.approvalText}>Finance</Text>
          </View>
          <View style={styles.approvalItem}>
            <MaterialCommunityIcons 
              name={item.legalApproval ? 'check-circle' : 'clock-outline'} 
              size={14} 
              color={item.legalApproval ? '#10B981' : '#F59E0B'} 
            />
            <Text style={styles.approvalText}>Legal</Text>
          </View>
          {item.totalPackage > 150000 && (
            <View style={styles.approvalItem}>
              <MaterialCommunityIcons 
                name={item.ceoApproval ? 'check-circle' : 'clock-outline'} 
                size={14} 
                color={item.ceoApproval ? '#10B981' : '#F59E0B'} 
              />
              <Text style={styles.approvalText}>CEO</Text>
            </View>
          )}
        </View>
      </View>

      {/* Risk & Success Factors */}
      {(item.riskFactors.length > 0 || item.successFactors.length > 0) && (
        <View style={styles.factorsContainer}>
          {item.riskFactors.length > 0 && (
            <View style={styles.riskFactors}>
              <Text style={styles.factorsTitle}>Risk Factors:</Text>
              <View style={styles.factorsList}>
                {item.riskFactors.map((factor, index) => (
                  <View key={index} style={styles.riskFactor}>
                    <MaterialCommunityIcons name="alert-circle" size={12} color="#EF4444" />
                    <Text style={styles.riskFactorText}>{factor}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}
          
          {item.successFactors.length > 0 && (
            <View style={styles.successFactors}>
              <Text style={styles.factorsTitle}>Success Factors:</Text>
              <View style={styles.factorsList}>
                {item.successFactors.map((factor, index) => (
                  <View key={index} style={styles.successFactor}>
                    <MaterialCommunityIcons name="check-circle" size={12} color="#10B981" />
                    <Text style={styles.successFactorText}>{factor}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}
        </View>
      )}

      {/* Previous Performance Summary */}
      <View style={styles.previousPerformanceContainer}>
        <Text style={styles.previousPerformanceTitle}>Candidate Performance</Text>
        <View style={styles.performanceMetrics}>
          <View style={styles.performanceMetric}>
            <Text style={styles.performanceLabel}>Final Interview</Text>
            <Text style={styles.performanceValue}>{item.finalInterviewScore}%</Text>
          </View>
          <View style={styles.performanceMetric}>
            <Text style={styles.performanceLabel}>AR Demo</Text>
            <Text style={styles.performanceValue}>{item.arDemoScore}%</Text>
          </View>
          <View style={styles.performanceMetric}>
            <Text style={styles.performanceLabel}>Overall Rating</Text>
            <View style={styles.starsContainer}>
              {renderStars(item.overallRating)}
            </View>
          </View>
        </View>
      </View>

      {/* Actions */}
      <View style={styles.candidateActions}>
        {item.offerStatus === 'draft' && (
          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: '#8B5CF6' }]}
            onPress={() => handleGenerateSmartContract(item)}
          >
            <MaterialCommunityIcons name="file-document-outline" size={16} color="#FFFFFF" />
            <Text style={[styles.actionButtonText, { color: '#FFFFFF' }]}>Generate Contract</Text>
          </TouchableOpacity>
        )}
        {(item.offerStatus === 'pending_approval' || item.offerStatus === 'draft') && (
          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: '#3B82F6' }]}
            onPress={() => handleSendOffer(item)}
          >
            <MaterialCommunityIcons name="send" size={16} color="#FFFFFF" />
            <Text style={[styles.actionButtonText, { color: '#FFFFFF' }]}>Send Offer</Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => navigation.navigate('JobOfferDetailsScreen', { candidate: item })}
        >
          <MaterialCommunityIcons name="eye" size={16} color="#8B5CF6" />
          <Text style={styles.actionButtonText}>View Details</Text>
        </TouchableOpacity>
        {item.offerStatus === 'negotiating' && (
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => console.log('Continue negotiation:', item.id)}
          >
            <MaterialCommunityIcons name="handshake" size={16} color="#F59E0B" />
            <Text style={styles.actionButtonText}>Negotiate</Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          style={styles.moreButton}
          onPress={() => {
            Alert.alert(
              'Job Offer Actions',
              'Choose an action',
              [
                { text: 'Edit Offer', onPress: () => console.log('Edit offer') },
                { text: 'Withdraw Offer', onPress: () => console.log('Withdraw offer') },
                { text: 'Contact Candidate', onPress: () => console.log('Contact candidate') },
                { text: 'Cancel', style: 'cancel' },
              ]
            );
          }}
        >
          <MaterialCommunityIcons name="dots-vertical" size={20} color="#6B7280" />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#8B5CF6" />
          <Text style={styles.loadingText}>Loading job offers...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#8B5CF6" />
      
      {/* Header */}
      <LinearGradient
        colors={['#8B5CF6', '#7C3AED']}
        style={styles.header}
      >
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          
          <View style={styles.headerInfo}>
            <View style={styles.headerTitleContainer}>
              <MaterialCommunityIcons name="file-document-outline" size={24} color="#FFFFFF" />
              <Text style={styles.headerTitle}>Job Offers</Text>
            </View>
            <Text style={styles.headerSubtitle}>
              {sortedCandidates.length} Offers • Smart Contract
            </Text>
          </View>
          
          <TouchableOpacity
            style={styles.smartContractButton}
            onPress={() => setShowSmartContractModal(true)}
          >
            <MaterialCommunityIcons name="file-document-outline" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {/* Search and Sort */}
      <View style={styles.searchSortContainer}>
        <View style={styles.searchBar}>
          <MaterialCommunityIcons name="magnify" size={20} color="#6B7280" />
          <TextInput
            style={styles.searchInput}
            placeholder="Search job offers..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor="#9CA3AF"
          />
        </View>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.sortContainer}
        >
          {sortOptions.map((option) => (
            <TouchableOpacity
              key={option.id}
              style={[
                styles.sortOption,
                sortBy === option.id && styles.activeSortOption,
              ]}
              onPress={() => setSortBy(option.id)}
            >
              <MaterialCommunityIcons
                name={option.icon}
                size={16}
                color={sortBy === option.id ? '#FFFFFF' : '#6B7280'}
              />
              <Text
                style={[
                  styles.sortOptionText,
                  sortBy === option.id && styles.activeSortOptionText,
                ]}
              >
                {option.name}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Tabs */}
      <View style={styles.tabsContainer}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.tabsContent}
        >
          {tabs.map((tab) => (
            <TouchableOpacity
              key={tab.id}
              style={[
                styles.tab,
                selectedTab === tab.id && styles.activeTab,
              ]}
              onPress={() => setSelectedTab(tab.id)}
            >
              <Text
                style={[
                  styles.tabText,
                  selectedTab === tab.id && styles.activeTabText,
                ]}
              >
                {tab.name}
              </Text>
              <View
                style={[
                  styles.tabBadge,
                  selectedTab === tab.id && styles.activeTabBadge,
                ]}
              >
                <Text
                  style={[
                    styles.tabBadgeText,
                    selectedTab === tab.id && styles.activeTabBadgeText,
                  ]}
                >
                  {selectedTab === tab.id ? sortedCandidates.length : tab.count}
                </Text>
              </View>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Bulk Actions */}
      {selectedCandidates.length > 0 && (
        <View style={styles.bulkActionsContainer}>
          <Text style={styles.bulkActionsText}>
            {selectedCandidates.length} selected
          </Text>
          <View style={styles.bulkActions}>
            <TouchableOpacity
              style={styles.bulkActionButton}
              onPress={() => setShowSmartContractModal(true)}
            >
              <MaterialCommunityIcons name="file-document-outline" size={16} color="#FFFFFF" />
              <Text style={styles.bulkActionText}>Smart Contract</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.bulkActionButton, { backgroundColor: '#10B981' }]}
              onPress={() => handleBulkAction('Move to Accepted Offers')}
            >
              <MaterialCommunityIcons name="arrow-right" size={16} color="#FFFFFF" />
              <Text style={styles.bulkActionText}>Move to Accepted</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Candidates List */}
      <FlatList
        data={sortedCandidates}
        keyExtractor={(item) => item.id}
        renderItem={renderCandidateCard}
        style={styles.candidatesList}
        contentContainerStyle={styles.candidatesListContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        ListEmptyComponent={() => (
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="file-document-outline" size={64} color="#D1D5DB" />
            <Text style={styles.emptyTitle}>No job offers found</Text>
            <Text style={styles.emptySubtitle}>
              {searchQuery
                ? 'Try adjusting your search criteria'
                : 'No offers match the current criteria'}
            </Text>
          </View>
        )}
      />

      {/* Floating Action Button */}
      <TouchableOpacity
        style={styles.fab}
        onPress={() => navigation.navigate('AcceptedOffersScreen', {
          totalOffers: sortedCandidates.filter(c => c.offerStatus === 'accepted').length,
        })}
      >
        <MaterialCommunityIcons name="arrow-right" size={24} color="#FFFFFF" />
      </TouchableOpacity>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
  },
  header: {
    paddingTop: 20,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerInfo: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 4,
  },
  smartContractButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchSortContainer: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 12,
  },
  searchInput: {
    flex: 1,
    marginLeft: 12,
    fontSize: 16,
    color: '#1F2937',
  },
  sortContainer: {
    flexDirection: 'row',
  },
  sortOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginRight: 8,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
  },
  activeSortOption: {
    backgroundColor: '#8B5CF6',
  },
  sortOptionText: {
    marginLeft: 4,
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
  },
  activeSortOptionText: {
    color: '#FFFFFF',
  },
  tabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tabsContent: {
    paddingHorizontal: 20,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
  },
  activeTab: {
    backgroundColor: '#EDE9FE',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
  },
  activeTabText: {
    color: '#8B5CF6',
  },
  tabBadge: {
    marginLeft: 8,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    backgroundColor: '#E5E7EB',
  },
  activeTabBadge: {
    backgroundColor: '#8B5CF6',
  },
  tabBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6B7280',
  },
  activeTabBadgeText: {
    color: '#FFFFFF',
  },
  bulkActionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#8B5CF6',
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  bulkActionsText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  bulkActions: {
    flexDirection: 'row',
  },
  bulkActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#7C3AED',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginLeft: 8,
  },
  bulkActionText: {
    marginLeft: 4,
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  candidatesList: {
    flex: 1,
  },
  candidatesListContent: {
    padding: 20,
  },
  candidateCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  selectedCard: {
    borderWidth: 2,
    borderColor: '#8B5CF6',
  },
  checkbox: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 1,
  },
  candidateHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    marginRight: 12,
  },
  candidateInfo: {
    flex: 1,
  },
  candidateNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 4,
  },
  candidateName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
    marginRight: 8,
  },
  candidateBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  tagBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: '#EDE9FE',
    borderRadius: 8,
    marginRight: 4,
    marginBottom: 2,
  },
  tagText: {
    marginLeft: 2,
    fontSize: 10,
    fontWeight: '600',
    color: '#8B5CF6',
  },
  candidatePosition: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 2,
  },
  candidateCompany: {
    fontSize: 12,
    color: '#6B7280',
  },
  candidateStatus: {
    alignItems: 'flex-end',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  offerPackageContainer: {
    backgroundColor: '#EDE9FE',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#8B5CF6',
  },
  offerPackageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  offerPackageTitle: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  packageValueBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#8B5CF6',
    borderRadius: 12,
  },
  packageValueText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  packageBreakdown: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  packageItem: {
    alignItems: 'center',
  },
  packageLabel: {
    fontSize: 10,
    color: '#6B7280',
    marginTop: 2,
  },
  packageValue: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#1F2937',
    marginTop: 2,
  },
  smartContractContainer: {
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#8B5CF6',
  },
  smartContractHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  smartContractTitle: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  contractStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  contractStatusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  contractDetails: {
    marginTop: 8,
  },
  contractTemplateName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  contractDescription: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 8,
  },
  contractFeatures: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 8,
  },
  contractFeature: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
    marginBottom: 4,
  },
  contractFeatureText: {
    marginLeft: 4,
    fontSize: 11,
    color: '#6B7280',
  },
  contractAddressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  contractAddressLabel: {
    marginLeft: 4,
    fontSize: 11,
    color: '#6B7280',
    marginRight: 4,
  },
  contractAddress: {
    fontSize: 11,
    fontFamily: 'monospace',
    color: '#8B5CF6',
    fontWeight: '600',
  },
  offerTimelineContainer: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  timelineHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  timelineTitle: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  timelineItems: {
    marginTop: 4,
  },
  timelineItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  timelineLabel: {
    marginLeft: 8,
    fontSize: 12,
    color: '#6B7280',
    width: 100,
  },
  timelineValue: {
    fontSize: 12,
    color: '#374151',
    fontWeight: '500',
  },
  negotiationContainer: {
    backgroundColor: '#FEF3E2',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  negotiationHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  negotiationTitle: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  negotiationRoundsBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#F59E0B',
    borderRadius: 12,
  },
  negotiationRoundsText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  negotiationDetails: {
    marginTop: 4,
  },
  negotiationPoint: {
    fontSize: 12,
    color: '#374151',
    marginBottom: 2,
  },
  negotiationComplexity: {
    fontSize: 12,
    color: '#6B7280',
  },
  offerMetricsContainer: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  metricsHeader: {
    marginBottom: 8,
  },
  metricsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  metricItem: {
    alignItems: 'center',
    width: '48%',
    marginBottom: 8,
  },
  metricLabel: {
    fontSize: 10,
    color: '#6B7280',
    marginTop: 2,
    textAlign: 'center',
  },
  metricValue: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#1F2937',
    marginTop: 2,
  },
  approvalContainer: {
    marginBottom: 12,
  },
  approvalTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  approvalItems: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  approvalItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
    marginBottom: 4,
  },
  approvalText: {
    marginLeft: 4,
    fontSize: 11,
    color: '#6B7280',
  },
  factorsContainer: {
    marginBottom: 12,
  },
  riskFactors: {
    marginBottom: 8,
  },
  successFactors: {
    marginBottom: 4,
  },
  factorsTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 4,
  },
  factorsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  riskFactor: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: '#FEE2E2',
    borderRadius: 8,
    marginRight: 4,
    marginBottom: 2,
  },
  riskFactorText: {
    marginLeft: 2,
    fontSize: 10,
    color: '#EF4444',
  },
  successFactor: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: '#D1FAE5',
    borderRadius: 8,
    marginRight: 4,
    marginBottom: 2,
  },
  successFactorText: {
    marginLeft: 2,
    fontSize: 10,
    color: '#10B981',
  },
  previousPerformanceContainer: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  previousPerformanceTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  performanceMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  performanceMetric: {
    alignItems: 'center',
  },
  performanceLabel: {
    fontSize: 10,
    color: '#6B7280',
  },
  performanceValue: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#10B981',
    marginTop: 2,
  },
  starsContainer: {
    flexDirection: 'row',
    marginTop: 2,
  },
  candidateActions: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#F3F4F6',
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 4,
  },
  actionButtonText: {
    marginLeft: 4,
    fontSize: 11,
    color: '#6B7280',
    fontWeight: '500',
  },
  moreButton: {
    padding: 6,
    marginLeft: 'auto',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#374151',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    paddingHorizontal: 40,
  },
  fab: {
    position: 'absolute',
    bottom: 30,
    right: 30,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#8B5CF6',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
});
